import { useLayoutEffect, useRef } from "react";
import type { SortingState } from "@tanstack/react-table";
import { isEqual } from "@common/helpers/comparison";
import type { GridController } from "../focus/gridController";
import { canAnimateMovement, readTableMotion } from "./tableMotion";

interface RowOrderMotionInput {
    controller: GridController;
    /** `motion.animateRowOrder`. */
    isEnabled: boolean;
    sorting: SortingState;
    /** Body offset of every rendered row by id, this render. */
    positions: ReadonlyMap<string, number>;
}

/**
 * Opt-in FLIP for rendered rows after a sort or a row drop: each row that
 * was rendered before slides from its old offset, rows that just mounted
 * fade in. A sort that returns a page of all-new rows fades them all in;
 * a row that only mounts while the sort waits isn't a move. Only the
 * rendered window animates (a few dozen rows, never the
 * whole table), on the compositor, and never under reduced motion.
 */
export const useRowOrderMotion = ({
    controller,
    isEnabled,
    sorting,
    positions,
}: RowOrderMotionInput) => {
    const previousRef = useRef(positions);
    const sortingRef = useRef(sorting);

    useLayoutEffect(() => {
        const previous = previousRef.current;
        previousRef.current = positions;
        const isSortChange = !isEqual(sortingRef.current, sorting);
        sortingRef.current = sorting;
        const { body, rowMotion } = controller;
        if (!isEnabled || !body) {
            return;
        }
        if (isSortChange) {
            rowMotion.requestReorder(null);
        }
        if (!rowMotion.isReorderRequested()) {
            return;
        }
        const droppedRowId = rowMotion.getDroppedRowId();
        const rendered = [...positions];
        const hasMoves = rendered.some(
            ([rowId, offset]) =>
                previous.has(rowId) && previous.get(rowId) !== offset,
        );
        const isPageReplaced =
            rendered.length > 0 &&
            rendered.every(([rowId]) => !previous.has(rowId));
        if (!hasMoves && !isPageReplaced) {
            return;
        }
        rowMotion.clearReorderRequest();
        const motion = readTableMotion(body);
        if (!canAnimateMovement(motion)) {
            return;
        }
        // Rows that travel further than a screen start one screen away.
        const reach = controller.viewport?.clientHeight ?? 0;
        for (const element of body.querySelectorAll<HTMLElement>(
            ":scope > [data-row-id]",
        )) {
            const rowId = element.dataset.rowId ?? "";
            const to = positions.get(rowId);
            const from = previous.get(rowId);
            if (to === undefined || from === to || rowId === droppedRowId) {
                continue;
            }
            if (from === undefined) {
                element.animate([{ opacity: 0 }, { opacity: 1 }], {
                    duration: motion.fastMs,
                    easing: motion.easeOut,
                });
                continue;
            }
            const start = Math.min(Math.max(from, to - reach), to + reach);
            element.animate(
                [
                    { transform: `translateY(${start}px)` },
                    { transform: `translateY(${to}px)` },
                ],
                { duration: motion.mediumMs, easing: motion.easeInOut },
            );
        }
    });
};
